import React, { useMemo } from 'react';
import { AlarmClockIcon } from 'lucide-react';
import { useCountdown } from '../hooks/useCountdown';
import { getOfferDeadline, spotsTakenPercent } from '../utils/offer';
function pad(value: number): string {
  return value.toString().padStart(2, '0');
}

/**
 * Warm terracotta urgency block that sits directly above the price card.
 */
export function CountdownTimer() {
  const deadline = useMemo(() => getOfferDeadline(), []);
  const {
    hours,
    minutes,
    seconds,
    isExpired
  } = useCountdown(deadline);
  const units = [{
    value: hours,
    label: 'hrs'
  }, {
    value: minutes,
    label: 'min'
  }, {
    value: seconds,
    label: 'sec'
  }];
  return <div className="mx-auto max-w-xl rounded-4xl border border-terracotta/40 bg-terracotta px-6 py-7 text-center shadow-lift sm:px-9" data-id="element-26" data-mp="15dwb4l8rhshq">
      <p className="flex items-center justify-center gap-2 font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-ivory" data-id="element-27" data-mp="34pgxyxqta6my">
        <AlarmClockIcon className="h-4 w-4 text-gold-soft" strokeWidth={1.9} aria-hidden="true" data-id="element-28" data-mp="2zpujym35smd3" />
        The launch price ends in:
      </p>

      {isExpired ? <p className="mt-4 font-display text-xl leading-snug text-ivory" data-id="element-29" data-mp="1h0jeqg62239o">
          Today’s launch price has ended.
        </p> : <>
          <div className="mt-5 flex items-center justify-center gap-2 sm:gap-3" aria-hidden="true" data-id="element-30" data-mp="124lplhujtz6y">
            {units.map((unit, index) => <React.Fragment key={unit.label} data-id="element-31" data-mp="1urg7q83w8mh9" data-mpi={index}>
                <div className="flex min-w-[4.5rem] items-baseline justify-center gap-1 rounded-2xl bg-shell px-3 py-2.5 sm:min-w-[5.5rem]" data-id="element-32" data-mp="1uroi9tcjnfpa">
                  <span className="font-display text-3xl leading-none text-ink sm:text-4xl" data-id="element-33" data-mp="1l1d65jipi07b">
                    {pad(unit.value)}
                  </span>
                  <span className="font-sans text-[10px] font-semibold uppercase tracking-[0.1em] text-stone" data-id="element-34" data-mp="1pyvf39wq4ffb">
                    {unit.label}
                  </span>
                </div>
                {index < units.length - 1 ? <span className="font-display text-2xl text-ivory/70" data-id="element-35" data-mp="2iitzabu80e16">:</span> : null}
              </React.Fragment>)}
          </div>
          <p className="sr-only" role="timer" data-id="element-36" data-mp="2ndwdtpmc1drm">
            {hours} hours, {minutes} minutes and {seconds} seconds left at the
            launch price.
          </p>

          <p className="mx-auto mt-5 max-w-sm font-sans text-[0.82rem] font-medium leading-snug text-ivory" data-id="element-37" data-mp="2je5o7p8jl3oo">
            After this the price goes up — and it will not come back down.
          </p>

          <div className="mt-6 border-t border-ivory/25 pt-5" data-id="element-38" data-mp="29fycplx0vdu2">
            <div className="flex items-baseline justify-between gap-4 font-sans text-[10px] font-semibold uppercase tracking-[0.14em] text-ivory/85" data-id="element-39" data-mp="24mda1gkhhvoj">
              <span data-id="element-40" data-mp="1vmgss00fbz3i">Launch price spots remaining</span>
              <span className="text-gold-soft" data-id="element-41" data-mp="1vnryi5dfjgx9">Only a few left</span>
            </div>
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-ivory/25" role="progressbar" aria-valuenow={spotsTakenPercent} aria-valuemin={0} aria-valuemax={100} aria-label="Launch price spots taken" data-id="element-42" data-mp="1qmcaq5jvawb6">
              <div className="h-full rounded-full bg-gold" style={{
            width: `${spotsTakenPercent}%`
          }} data-id="element-43" data-mp="15qgy0096rq7" />
            </div>
            <p className="mt-2 text-right font-sans text-[11px] text-ivory/80" data-id="element-44" data-mp="b4fz7tw2ae6z">
              {spotsTakenPercent}% of spots taken
            </p>
          </div>
        </>}
    </div>;
}